import React, { useEffect, useRef, useState } from "react";
import { runningOn, setRunning } from "./presenceHandover";

const UNLIT = { current: 0 };

// The page-wide layer the dust draws on (presenceScene.js), in the page's
// markup as it is built, so the built homepage's own script can start the
// dust before React has attached (presenceBoot.js). Once React has, it takes
// that scene over, or starts one where none runs, as after coming to the
// homepage from another page, or once the visitor's settings have changed.
// `reducedMotion`, `strength` and `spotlight` are as presenceScene.js has them.
const PresenceField = ({ reducedMotion = false, strength = 1, spotlight = UNLIT }) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    let scene = null;
    let left = false;
    const follow = (running) => {
      scene = running;
      setRunning(canvas, scene);
      scene.spotlight = spotlight;
      scene.onFailed = () => setFailed(true);
      setDebugInfo(scene.debugInfo);
      if (scene.failed) setFailed(true);
    };
    const booted = runningOn(canvas);
    if (booted?.reducedMotion === reducedMotion && booted.strength === strength) follow(booted);
    else {
      booted?.stop();
      setRunning(canvas, null);
      // Fetched ahead of the page's images, as part of its first view: on a
      // connection the browser finds slow, it holds back what it ranks low
      // while other requests are on their way.
      import(/* webpackFetchPriority: "high" */ "./presenceScene").then(
        ({ startPresence }) => {
          if (left) return;
          // The built page's script may have started it meanwhile.
          follow(runningOn(canvas) ?? startPresence(wrapper, canvas, { reducedMotion, strength }));
        },
        (error) => {
          console.error("The page's dust is not drawn:", error);
          setFailed(true);
        },
      );
    }
    return () => {
      left = true;
      // The built page's script may have started it, React's still on its way.
      (scene ?? runningOn(canvas))?.stop();
      setRunning(canvas, null);
    };
  }, [reducedMotion, strength, spotlight]);

  if (failed) return debugInfo ? <DebugPanel info={debugInfo} /> : null;

  return (
    <>
      {debugInfo && <DebugPanel info={debugInfo} />}
      <div
        ref={wrapperRef}
        aria-hidden
        data-dust-strength={strength}
        // As tall as the largest viewport, not the visible one: phone
        // browsers grow and shrink the visible height as their toolbars slide
        // during a scroll, and a layer that followed it would re-fit its
        // scene each time, zooming the whole stage mid-scroll.
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100lvh",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
      </div>
    </>
  );
};

// Debug-only HUD (shown for ?pfsolo / ?pfdebug / ?forcedust). Never rendered
// for normal visitors.
const DebugPanel = ({ info }) => (
  <div
    style={{
      position: "fixed",
      top: 8,
      left: 8,
      zIndex: 99999,
      font: "12px/1.5 monospace",
      color: "#fff",
      background: "rgba(0,0,0,0.78)",
      padding: "8px 10px",
      whiteSpace: "pre",
      pointerEvents: "none",
      maxWidth: "94vw",
      borderRadius: 6,
    }}
  >
    {[
      `SOLO: ${info.solo}`,
      `GPU: ${info.gpu}`,
      `WebGL: ${info.version}`,
      `program precision: ${info.progPrecision}`,
      `highp  vertex: ${info.vertHighp}   fragment: ${info.fragHighp}`,
      `max vertex attribs: ${info.maxAttribs}   max varyings: ${info.maxVarying}`,
      `instanced arrays: ${info.instanced}`,
    ].join("\n")}
  </div>
);

export default PresenceField;
